import React from 'react'

import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import routesRefinanciamento from '@routes/routes'
import ArrowLeft from '@interco/icons/core/action-navigation/ic_arrow_left'
import Help from '@interco/icons/core/status/ic_help_circle'
import { ApplicationState } from '@store/types'
import { validaEnvHomeByBridge } from '@utils/functions'
import { rootBackPaths } from '@utils/rootBackPaths'
import { BridgeService } from '@services/bridge'
import Tag from '@utils/Tag'
import { ConveniosCodes } from '@utils/enums'
import { setBottomsheetOrigin } from '@store/refinanciamento/simulacao/actions'

import * as S from './styles'

export type HeaderProps = {
  children: React.ReactNode
  rightIcon?: 'house' | 'download' | 'more' | 'question-mark' | React.ReactNode
  onRightButtonClick?: () => void
  showLeftIcon?: boolean
  showRightIcon?: boolean
  headerPosition?: string
}

type RightIconProps = Pick<HeaderProps, 'rightIcon'>

const RightIcon = ({ rightIcon }: RightIconProps): JSX.Element => {
  if (typeof rightIcon === 'string') {
    if (rightIcon === 'question-mark')
      return <Help color="var(--primary500)" width={24} height={24} aria-hidden="true" />
  }

  if (rightIcon) return rightIcon as JSX.Element
  return <S.CleanIcon />
}

const Header = ({
  children,
  rightIcon,
  onRightButtonClick,
  showLeftIcon = true,
  showRightIcon = false,
  headerPosition = '',
}: HeaderProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isApp, origemGoBack } = useSelector((state: ApplicationState) => state.ui.navigation)
  const { convenioCodigo } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )
  const origemGoBackValue = String(origemGoBack || '')
  const shouldHandleAsRootBack = rootBackPaths.includes(location?.pathname || '')
  const isNotFirstScreen = location?.key !== 'default'
  const shouldRedirectToEmprestimoFromContratos = () => {
    if (location?.pathname !== routesRefinanciamento.CONTRATOS) return false
    if (!isApp) return false
    if (origemGoBackValue !== 'emprestimo') return false
    return isNotFirstScreen
  }

  const handleStatusDocumentoBack = () => {
    if (convenioCodigo === ConveniosCodes.INSS || convenioCodigo === ConveniosCodes.CTPS) {
      dispatch(setBottomsheetOrigin(true))
    }
    navigate(routesRefinanciamento.RESULTADO_SIMULACAO)
  }

  const handleResultadoSimulacaoBack = () => {
    navigate(routesRefinanciamento.CONTRATOS)
  }

  const handleTutorialBack = () => {
    Tag.enviarTagueamentoApp({
      screen: 'clt:refin:header',
      ref_type: 'botao',
      content_action: 'Toque',
      action_id: 'Voltar',
      section: 'header',
      sub_section: '',
      step: '',
      scenario: 'Voltar',
      ref_figma: '2',
    })
    const { from } = (location.state ?? {}) as { from?: string }
    const knownRoutes = Object.values(routesRefinanciamento) as string[]
    const isKnownRoute = Boolean(from && knownRoutes.includes(from))
    navigate(isKnownRoute && from ? from : routesRefinanciamento.SUCESSO)
  }

  const handleRootBack = () => {
    if (
      origemGoBackValue === 'emprestimo' ||
      origemGoBackValue === 'emprestimoinss' ||
      origemGoBackValue === 'emprestimosanguelaranja' ||
      origemGoBackValue === 'consignadoHome'
    ) {
      window.location.href = validaEnvHomeByBridge(origemGoBackValue, isApp) || ''
      return
    }

    BridgeService.backToNative()
  }

  const handleDefaultBack = () => {
    Tag.enviarTagueamentoApp({
      screen: 'clt:refin:header',
      ref_type: 'botao',
      content_action: 'Toque',
      action_id: 'Voltar',
      section: 'header',
      sub_section: '',
      step: '',
      scenario: 'Voltar',
      ref_figma: '2',
    })

    navigate(-1)
  }

  const onBackClick = () => {
    if (location?.pathname === routesRefinanciamento.STATUS_DOCUMENTO) {
      handleStatusDocumentoBack()
      return
    }

    if (location?.pathname === routesRefinanciamento.RESULTADO_SIMULACAO) {
      handleResultadoSimulacaoBack()
      return
    }

    if (location?.pathname === routesRefinanciamento.TUTORIAL) {
      handleTutorialBack()
      return
    }

    if (shouldRedirectToEmprestimoFromContratos()) {
      window.location.href = validaEnvHomeByBridge(origemGoBackValue, isApp) || ''
      return
    }

    if (shouldHandleAsRootBack) {
      handleRootBack()
      return
    }

    handleDefaultBack()
  }

  const rightButtonLabel =
    rightIcon === 'question-mark' ? 'Ajuda - perguntas frequentes' : undefined

  return (
    <S.Container headerPosition={headerPosition}>
      {showLeftIcon ? (
        <S.IconButton type="button" name="left-buttom" dir="left-button" onClick={onBackClick}>
          <ArrowLeft color="var(--primary500)" width={24} height={24} />
        </S.IconButton>
      ) : (
        showRightIcon && <S.CleanIcon aria-hidden="true" />
      )}
      <S.Title>{children}</S.Title>
      {showRightIcon ? (
        <S.IconButton
          type="button"
          name="right-button"
          dir="right-button"
          aria-label={rightButtonLabel}
          onClick={onRightButtonClick}
        >
          <RightIcon rightIcon={rightIcon} />
        </S.IconButton>
      ) : (
        <S.CleanIcon aria-hidden="true" />
      )}
    </S.Container>
  )
}

export default Header
